import { useCallback, useEffect, useRef, useState } from "react";
import { authChannelName, notifySessionChanged } from "../authEvents";

const collection = (agentId) => `/api/portal/conversations?agent_id=${encodeURIComponent(agentId)}`;

export default function usePersonalConversations(agentId, guestHistory, guestMessages) {
  const [user, setUser] = useState(null);
  const [mode, setMode] = useState("loading");
  const [history, setHistory] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const queue = useRef(Promise.resolve());
  const alive = useRef(true);
  const pendingSaves = useRef(0);
  const lastSaveFailure = useRef(null);
  const invalidated = useRef(false);

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    (async () => {
      try {
        const me = await fetch("/api/auth/me", { signal: controller.signal, credentials: "same-origin" });
        if (!me.ok) throw new Error("认证服务暂不可用，请稍后重新进入");
        const identity = await me.json();
        notifySessionChanged();
        if (!identity.authenticated) {
          setHistory(guestHistory);
          setMessages(guestMessages);
          setMode("guest");
          return;
        }
        const response = await fetch(collection(agentId), {
          signal: controller.signal, credentials: "same-origin",
          headers: { "X-Portal-User": identity.user.uid },
        });
        if (!response.ok) throw new Error("个人对话读取失败，请重新登录或稍后重试");
        const data = await response.json();
        setUser(identity.user);
        setHistory(data.conversations.map((item) => ({ ...item, time: "已保存", active: false })));
        setMode("personal");
      } catch (failure) {
        if (failure.name !== "AbortError" && alive.current) {
          setMode("error");
          setError(failure.message);
        }
      }
    })();
    return () => { alive.current = false; controller.abort(); };
  }, [agentId, guestHistory, guestMessages]);

  const api = useCallback(async (path, options = {}) => {
    if (invalidated.current) throw new Error("请重新登录个人账号");
    const response = await fetch(path, {
      ...options, credentials: "same-origin",
      headers: {
        Accept: "application/json", "Content-Type": "application/json",
        "X-Portal-Request": "1", "X-Portal-User": user.uid,
      },
    });
    if (response.status === 401) {
      invalidated.current = true;
      if (alive.current) {
        setMessages([]);
        setHistory([]);
        setConversationId(null);
        setMode("expired");
        setError("登录已过期或账号已切换，请重新登录");
      }
      throw new Error("请重新登录个人账号");
    }
    if (response.status === 204) return null;
    const data = await response.json().catch(() => ({ message: "对话服务暂不可用，请稍后重试" }));
    if (invalidated.current) throw new Error("请重新登录个人账号");
    if (!response.ok) throw new Error(data.message || "对话保存失败，请重试");
    return data;
  }, [user]);

  useEffect(() => {
    if (!user || mode !== "personal") return;
    const checkIdentity = async () => {
      try {
        const response = await fetch("/api/auth/me", { credentials: "same-origin" });
        if (!response.ok || !alive.current) return;
        const identity = await response.json();
        if (!identity.authenticated || identity.user.uid !== user.uid) {
          invalidated.current = true;
          setMessages([]); setHistory([]); setConversationId(null);
          setMode("expired"); setError("登录已过期或账号已切换，请重新登录");
        }
      } catch { /* Keep the current workspace during a transient network failure. */ }
    };
    const checkWhenVisible = () => { if (!document.hidden) checkIdentity(); };
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(authChannelName);
    if (channel) channel.onmessage = checkIdentity;
    window.addEventListener("focus", checkIdentity);
    document.addEventListener("visibilitychange", checkWhenVisible);
    return () => {
      window.removeEventListener("focus", checkIdentity);
      document.removeEventListener("visibilitychange", checkWhenVisible);
      channel?.close();
    };
  }, [mode, user]);

  useEffect(() => {
    if (mode !== "personal" || !conversationId || !messages.length) return;
    const title = (messages.find((item) => item.role === "user")?.content || "新对话").slice(0, 140);
    const snapshot = { agentId, title, messages };
    setSaveState("saving");
    pendingSaves.current += 1;
    queue.current = queue.current.catch(() => {}).then(async () => {
      if (!alive.current) return;
      await api(`/api/portal/conversations/${conversationId}`, {
        method: "PUT", body: JSON.stringify(snapshot),
      });
      if (alive.current) {
        lastSaveFailure.current = null;
        setError("");
        setHistory((items) => items.map((item) => item.id === conversationId
          ? { ...item, title, time: "已保存" } : item));
      }
    }).catch((failure) => {
      lastSaveFailure.current = failure;
      if (alive.current) { setError(failure.message); }
    }).finally(() => {
      pendingSaves.current -= 1;
      if (alive.current && !pendingSaves.current) setSaveState(lastSaveFailure.current ? "error" : "saved");
    });
  }, [agentId, api, conversationId, messages, mode, retry]);

  const ensureConversation = (content) => {
    if (mode !== "personal" || conversationId) return;
    const id = crypto.randomUUID();
    setConversationId(id);
    setHistory((items) => [
      { id, agentId, title: content.slice(0, 140), time: "保存中", active: true },
      ...items.map((item) => ({ ...item, active: false })),
    ]);
  };

  const newConversation = () => {
    setConversationId(null);
    setMessages([]);
    setHistory((items) => items.map((item) => ({ ...item, active: false })));
    setSaveState("idle");
    setError("");
  };

  const openConversation = async (item) => {
    if (busy) return;
    if (mode === "guest") {
      setHistory((items) => items.map((candidate) => ({ ...candidate, active: candidate.id === item.id })));
      setMessages(item.id === "current" ? guestMessages : [
        { id: `${item.id}-user`, role: "user", time: "演示", content: item.title },
        { id: `${item.id}-assistant`, role: "assistant", time: "演示",
          content: `这是「${item.title}」的演示对话。登录个人账号后，会读取你自己的历史记录。` },
      ]);
      return;
    }
    if (mode !== "personal") return;
    setBusy(true);
    try {
      await queue.current;
      if (lastSaveFailure.current) throw lastSaveFailure.current;
      const data = await api(`/api/portal/conversations/${item.id}`);
      setConversationId(item.id);
      setMessages(data.conversation.messages);
      setHistory((items) => items.map((candidate) => ({ ...candidate, active: candidate.id === item.id })));
      setError("");
    } catch (failure) {
      setError(failure.message);
    } finally { setBusy(false); }
  };

  const clearConversations = async () => {
    if (busy) return;
    if (mode === "guest") { newConversation(); setHistory([]); return; }
    if (mode !== "personal") return;
    setBusy(true);
    try {
      await queue.current;
      await api(collection(agentId), { method: "DELETE" });
      newConversation();
      setHistory([]);
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };

  return {
    user, mode, history, messages, setMessages, busy: busy || !["personal", "guest"].includes(mode),
    saveState, error, ensureConversation, newConversation, openConversation, clearConversations,
    retrySave: () => setRetry((value) => value + 1),
  };
}
